import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Controller } from 'react-hook-form';
import { Switch } from '@mui/material';
import InstructorCouponSection from '@/pages/Seminar/components/form/InstructorCouponSection';
import DateTimePicker from '@/components/common/datePicker/DateTimePicker';
import { oneMinuteAfter } from '@/components/common/datePicker/pickerUtils';
import {
  CalcBox,
  ErrorMessage,
  FORM_COLOR,
  FieldBlock,
  GuideLines,
  Label,
  StyledInput,
  ToggleRow,
  TwoColumnGrid,
  UnitInput,
} from '@/pages/Seminar/components/form/formStyles';
import { parseLocal } from '@/pages/Seminar/components/form/seminarFormUtils';
import { PLATFORM_FEE_RATE, resolvedStart } from '@/pages/Seminar/components/form/seminarFormModel';
import { useSeminarForm } from '@/pages/Seminar/components/form/steps/SeminarFormContext';

const won = (value: number): string => `${value.toLocaleString()}원`;

/**
 * 참가비·정산 예상·정원 배치 — 폰: 참가비 → 정산 예상 → 정원(세로), PC: 참가비|정원 한 줄 + 아래 정산 예상(시안 pc2).
 */
const PriceGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas: 'price' 'calc' 'capacity';
  column-gap: 12px;
  margin-bottom: 22px;

  > .price { grid-area: price; }
  > .calc { grid-area: calc; }
  > .capacity { grid-area: capacity; }

  > * {
    margin-bottom: 0;
  }

  > .calc:not(:empty) {
    margin-top: 8px;
  }

  > .capacity {
    margin-top: 22px;
  }

  @media (min-width: 769px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-areas: 'price capacity' 'calc calc';

    > .capacity {
      margin-top: 0;
    }
  }
`;

/** 3단계 — 참가비(+정산 예상)·정원·모집 기간·함께 만들 쿠폰 */
const StepRecruitPrice: React.FC = () => {
  const {
    control, errors, values, setValue, career, openSale, lockPriceSchedule, isEditMode, seminarId,
    couponDrafts, onCouponDraftsChange, couponDraftNotice, couponBasis, couponDeferred, couponUpdates, onCouponUpdatesChange,
    couponReloadKey,
  } = useSeminarForm();
  const price: number = Number(values.price) || 0;
  const fee: number = Math.round(price * PLATFORM_FEE_RATE);
  // 상시 동영상은 정원을 비우면 제한 없음 — 스위치로 비우고 채운다
  const [forceLimit, setForceLimit] = useState<boolean>(false);
  const capacityEmpty: boolean = values.capacity == null || String(values.capacity) === '';
  const unlimited: boolean = openSale && capacityEmpty && !forceLimit;
  // 모집 시작·마감은 세미나 시작 전(같은 시각까지)
  const seminarStart: Date | null = resolvedStart(values);

  return (
    <>
      <PriceGrid>
      <FieldBlock data-field="price" className="price">
        <Label htmlFor="seminar-price">참가비<span>*</span></Label>
        <Controller
          name="price"
          control={control}
          render={({ field }) => (
            <UnitInput>
              <StyledInput
                id="seminar-price"
                type="text"
                inputMode="numeric"
                // 커리어는 유료 필수라 '무료면 0' 안내를 숨긴다. 일반은 0(무료) 허용.
                placeholder={career ? '참가비를 입력해 주세요' : '무료면 0'}
                disabled={lockPriceSchedule}
                // 0은 직접 입력하면 '0'으로 표시하되, 기본값/미입력은 빈칸(placeholder)이라 '0'이 미리 채워지지 않는다.
                value={field.value != null ? field.value.toLocaleString() : ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  field.onChange(value === '' ? undefined : Number(value));
                }}
                aria-invalid={!!errors.price}
                hasError={!!errors.price}
              />
              <em>원</em>
            </UnitInput>
          )}
        />
        {errors.price && <ErrorMessage>{errors.price.message}</ErrorMessage>}
        {!career && <GuideLines>참가비는 보여 주기만 해요. 접수·결제는 외부에서 받아요.</GuideLines>}
        {lockPriceSchedule && <GuideLines>결제한 분이 있어 참가비는 바꿀 수 없어요.</GuideLines>}
      </FieldBlock>

      <div className="calc">
        {career && price > 0 && (
          <CalcBox aria-label="1명당 정산 예상">
            <div><span>참가비</span><span>{won(price)}</span></div>
            <div><span>플랫폼 수수료 {Math.round(PLATFORM_FEE_RATE * 100)}%</span><span>− {won(fee)}</span></div>
            <div className="total"><span>1명당 정산 예상</span><span>{won(price - fee)}</span></div>
          </CalcBox>
        )}
      </div>

      {career && (
        <FieldBlock data-field="capacity" className="capacity">
          <Label htmlFor="seminar-capacity">
            정원{openSale ? <small>선택</small> : <span>*</span>}
          </Label>
          {openSale && (
            <ToggleRow>
              인원 제한 없음
              <Switch
                checked={unlimited}
                onChange={(e) => {
                  if (e.target.checked) {
                    setForceLimit(false);
                    setValue('capacity', undefined as unknown as number, { shouldValidate: true });
                  } else {
                    setForceLimit(true);
                  }
                }}
                inputProps={{ 'aria-label': '인원 제한 없음' }}
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: FORM_COLOR.primary }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: FORM_COLOR.primary } }}
              />
            </ToggleRow>
          )}
          {!unlimited && (
            <Controller
              name="capacity"
              control={control}
              render={({ field }) => (
                <UnitInput>
                  <StyledInput
                    {...field}
                    id="seminar-capacity"
                    type="number"
                    inputMode="numeric"
                    placeholder="정원 수"
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : '')}
                    aria-invalid={!!errors.capacity}
                    hasError={!!errors.capacity}
                  />
                  <em>명</em>
                </UnitInput>
              )}
            />
          )}
          {errors.capacity && <ErrorMessage>{errors.capacity.message}</ErrorMessage>}
        </FieldBlock>
      )}
      </PriceGrid>

      {/* 모집 기간 — 커리어만 (일반은 신청을 받지 않는다). 상시 동영상은 판매 시작부터 판매 종료 전까지라 받지 않는다 */}
      {career && !openSale && (
        <FieldBlock>
          <Label as="div">모집 기간<span>*</span></Label>
          <TwoColumnGrid>
            <div data-field="recruitmentStartDateTime" style={{ minWidth: 0 }}>
              <Controller
                name="recruitmentStartDateTime"
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    mode="datetime"
                    title="모집 시작"
                    placeholder="모집 시작"
                    value={field.value || ''}
                    onChange={(value: string) => field.onChange(value)}
                    min={new Date()}
                    max={seminarStart}
                    hasError={!!errors.recruitmentStartDateTime}
                  />
                )}
              />
            </div>
            <div data-field="recruitmentEndDateTime" style={{ minWidth: 0 }}>
              <Controller
                name="recruitmentEndDateTime"
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    mode="datetime"
                    icon="clock"
                    title="모집 마감"
                    placeholder="모집 마감"
                    value={field.value || ''}
                    onChange={(value: string) => field.onChange(value)}
                    // 마감은 모집 시작보다 뒤, 세미나 시작 전(같은 시각까지)
                    min={oneMinuteAfter(parseLocal(values.recruitmentStartDateTime)) ?? new Date()}
                    max={seminarStart}
                    hasError={!!errors.recruitmentEndDateTime}
                  />
                )}
              />
            </div>
          </TwoColumnGrid>
          {errors.recruitmentStartDateTime && <ErrorMessage>{errors.recruitmentStartDateTime.message}</ErrorMessage>}
          {errors.recruitmentEndDateTime && <ErrorMessage>{errors.recruitmentEndDateTime.message}</ErrorMessage>}
          <GuideLines>모집은 세미나 시작 전에 끝나야 해요.</GuideLines>
        </FieldBlock>
      )}

      {/* 쿠폰 — 커리어 유료(참가비 0·미입력이면 숨김). 수정 화면은 바로 만들고, 신규 등록은 초안으로 담아 등록할 때 함께 만든다 */}
      {career && price > 0 && (
        <FieldBlock data-field="coupons">
          <InstructorCouponSection
            seminarId={isEditMode ? seminarId : undefined}
            // 쿠폰 창의 기본 기간·안내·달력 최대·검사·미리보기 모두 폼(앞 단계) 값 하나로
            listPrice={couponBasis.listPrice}
            recruitmentEnd={couponBasis.recruitmentEnd}
            drafts={couponDrafts}
            onDraftsChange={onCouponDraftsChange}
            deferred={couponDeferred}
            pendingUpdates={couponUpdates}
            onPendingUpdatesChange={onCouponUpdatesChange}
            reloadKey={couponReloadKey}
          />
        </FieldBlock>
      )}
      {!isEditMode && couponDraftNotice && couponDrafts.length === 0 && (
        <GuideLines>{couponDraftNotice}</GuideLines>
      )}
    </>
  );
};

export default StepRecruitPrice;
